/**
 * ConditionalR — watches dependency fields and shows/hides children.
 *
 * Evaluates an appears_when expression against the current form values.
 * Uses the expression parser + evaluator from the expression/ module.
 *
 * When pdfMode is true, conditionals are still evaluated so hidden fields
 * are not included in the PDF output.
 */

import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { ReactNode } from 'react';
import { parseExpression } from '../expression/parser';
import { evaluate } from '../expression/evaluator';
import { scopeToEntryPrefix } from '../expression/visibility';

interface Props {
  /** Boolean expression string from appears_when */
  expression: string | null | undefined;
  children: ReactNode;
  /** When true, show all branches regardless of condition (admin preview) */
  previewAll?: boolean;
  /**
   * The renderer namePrefix when this field sits inside a repeater entry
   * (e.g. "sponsor.immediate_family.entries.0"). Required for `[*]`
   * expressions to resolve against THIS entry rather than the whole array.
   */
  entryPrefix?: string;
}

export function ConditionalR({ expression, children, previewAll = false, entryPrefix }: Props) {
  const { watch } = useFormContext();

  // Parse the expression once (memo-ised — stable as long as expression string doesn't change)
  const ast = useMemo(() => parseExpression(expression), [expression]);

  // null expression means always show
  if (ast === null) return <>{children}</>;

  // Get all current form values for evaluation
  // watch() with no args returns the full form values as a nested object.
  // We spread it into a flat map below inside the evaluate call.
  const formValues = watch() as Record<string, unknown>;

  // Flatten nested form values for the evaluator, then scope `[*]` wildcard
  // lookups to the repeater entry being rendered (no-op outside repeaters).
  const flatValues = scopeToEntryPrefix(flattenValues(formValues), entryPrefix);

  const isVisible = previewAll || evaluate(ast, flatValues);

  if (!isVisible) return null;

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Flatten nested react-hook-form values into dot-notation keys
// ---------------------------------------------------------------------------

export function flattenValues(
  obj: Record<string, unknown>,
  prefix = '',
  result: Record<string, unknown> = {},
): Record<string, unknown> {
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value)) {
      flattenValues(value as Record<string, unknown>, path, result);
      // Also store the object itself so evaluator can check sub-keys
      result[path] = value;
    } else if (Array.isArray(value)) {
      result[path] = value;
      value.forEach((item, idx) => {
        if (item && typeof item === 'object') {
          flattenValues(item as Record<string, unknown>, `${path}.${idx}`, result);
        }
      });
    } else {
      result[path] = value;
    }
  }
  return result;
}
